import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { CONTACT_IMAGE, Reveal } from "./shared";
import { ContactForm } from "./ContactForm";

export const ContactSection = React.forwardRef(function ContactSection(_, ref) {
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const contentOpacity = useTransform(scrollYProgress, [0, 0.55, 1], [1, 1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section
      id="contact"
      ref={ref}
      className="w-full relative overflow-hidden min-h-screen snap-start flex items-center"
      data-testid="closing-cta-section"
    >
      {/* Full-bleed photograph + tint */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={CONTACT_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 60%" }}
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,20,12,0.55) 0%, rgba(10,20,12,0.65) 50%, rgba(10,20,12,0.55) 100%)",
          }}
        />
        <div
          className="absolute inset-0 mix-blend-multiply"
          style={{ backgroundColor: "rgba(31,122,64,0.32)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.22) 75%)",
          }}
        />
      </div>

      <motion.div
        className="relative w-full max-w-3xl mx-auto px-6 md:px-12 py-14 md:py-16 text-center"
        style={{ opacity: contentOpacity, y: contentY }}
      >
        <Reveal
          as="div"
          className="text-[11px] tracking-[0.28em] uppercase text-white/75 mb-5 md:mb-6 font-sans font-semibold"
        >
          Get in touch
        </Reveal>
        <Reveal
          as="h2"
          delay={0.05}
          duration={1.2}
          y={28}
          className="font-serif text-white text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.15] tracking-[-0.01em] font-light max-w-2xl mx-auto"
          data-testid="closing-heading"
        >
          Partner with us.
        </Reveal>
        <Reveal
          as="p"
          delay={0.15}
          duration={1.0}
          className="mt-4 md:mt-5 text-white/80 font-sans font-light text-sm md:text-[15px] leading-relaxed max-w-xl mx-auto"
        >
          Whether you&apos;re buying carbon credits, investing in impact, or
          shaping a CSR programme — tell us a little about you and we&apos;ll
          take it from there.
        </Reveal>

        <Reveal as="div" delay={0.22} duration={1.0} className="mt-7 md:mt-9">
          <ContactForm />
        </Reveal>

        <Reveal
          as="p"
          delay={0.3}
          duration={1.0}
          className="mt-7 md:mt-9 text-white/65 font-sans font-light text-xs md:text-[13px] leading-relaxed"
        >
          Alternatively, reach out to{" "}
          <a
            href="mailto:akash@matimitra.earth"
            className="text-white/90 hover:text-white underline underline-offset-4 transition-colors duration-300"
          >
            akash@matimitra.earth
          </a>{" "}
          or{" "}
          <a
            href="mailto:support@matimitra.earth"
            className="text-white/90 hover:text-white underline underline-offset-4 transition-colors duration-300"
          >
            support@matimitra.earth
          </a>{" "}
          for more info.
        </Reveal>

        <Reveal
          as="p"
          delay={0.4}
          duration={1.0}
          className="mt-3 md:mt-4 text-white/55 font-sans font-light text-[11px] md:text-[12px] leading-relaxed tracking-wide"
          data-testid="contact-company-info"
        >
          MatiMitra Carbon Solutions Pvt. Ltd.
          <span className="mx-2 text-white/30">·</span>
          CIN U20119OD2025PTC050168
          <span className="mx-2 text-white/30">·</span>
          Odisha, India
        </Reveal>

        <Reveal
          as="div"
          delay={0.45}
          duration={1.0}
          className="mt-2 md:mt-3 flex items-center justify-center gap-4 text-[11px] md:text-[12px] text-white/50 font-sans tracking-wide"
        >
          <Link
            to="/privacy"
            data-testid="contact-privacy-link"
            className="hover:text-white transition-colors duration-300"
          >
            Privacy
          </Link>
          <span aria-hidden="true" className="text-white/25">·</span>
          <Link
            to="/terms"
            data-testid="contact-terms-link"
            className="hover:text-white transition-colors duration-300"
          >
            Terms
          </Link>
        </Reveal>
      </motion.div>
    </section>
  );
});
